import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Briefcase, ShieldCheck, Loader2, ExternalLink, Star, DollarSign, GraduationCap, Clock, CalendarDays, Building2 } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHeader, SectionLabel } from "../components/site/Section";
import { Reveal } from "../components/site/Reveal";
import ApplicationForm from "../components/site/ApplicationForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { getJobs } from "../lib/api";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    getJobs().then(setJobs).catch(() => setJobs([])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page">
      <Seo title="Careers" description="Explore open positions at VectorBridge Group and join our national talent network for direct hire, contract, and executive opportunities." />
      <PageHeader
        label="C/01"
        kicker="Careers"
        title="Your next mission starts here."
        intro="Browse current openings or join our talent network so we can match you to roles across government, defense, healthcare, and commercial organizations nationwide."
      />

      {/* Open positions */}
      <section className="border-b border-border py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex items-end justify-between">
            <SectionLabel index="C/02">Open Positions</SectionLabel>
            {!loading && <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{jobs.length} open</span>}
          </div>

          {loading ? (
            <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
          ) : jobs.length === 0 ? (
            <Reveal>
              <div className="mt-10 border border-dashed border-border p-12 text-center">
                <p className="font-display text-2xl tracking-tight">No open positions right now.</p>
                <p className="mt-2 text-muted-foreground">Join our talent network below and we'll reach out when a role fits.</p>
              </div>
            </Reveal>
          ) : (
            <div className="mt-12 divide-y divide-border border-t border-border">
              {jobs.map((job, i) => (
                <Reveal key={job.id} delay={(i % 4) * 0.05}>
                  <button
                    onClick={() => setActive(job)}
                    data-testid={`job-row-${job.id}`}
                    className="group grid w-full items-center gap-4 py-8 text-left md:grid-cols-12"
                  >
                    <div className="md:col-span-6">
                      <div className="flex flex-wrap items-center gap-2">
                        {job.featured && <span className="inline-flex items-center gap-1 border border-brand px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-brand"><Star size={10} /> Featured</span>}
                        {job.hiring_status === "Contingent on Award" && <span className="border border-border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">Contingent on Award</span>}
                      </div>
                      <h3 className="mt-1 font-display text-2xl tracking-tight transition-colors group-hover:text-brand sm:text-3xl">{job.title}</h3>
                      {job.summary && <p className="mt-2 max-w-xl text-sm text-muted-foreground">{job.summary}</p>}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 md:col-span-5">
                      <Meta icon={MapPin} label={job.location} />
                      <Meta icon={Briefcase} label={job.employmentType} />
                      {job.work_arrangement && <Meta icon={Building2} label={job.work_arrangement} />}
                      {job.clearance_requirement && job.clearance_requirement !== "None" && <Meta icon={ShieldCheck} label={job.clearance_requirement} />}
                    </div>
                    <div className="md:col-span-1 md:justify-self-end">
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors group-hover:text-foreground">
                        View <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Talent network */}
      <section className="border-b border-border py-16 sm:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionLabel index="C/03">Talent Network</SectionLabel>
            <Reveal>
              <h2 className="mt-8 font-display text-3xl font-light leading-tight tracking-tighter sm:text-4xl">
                Don't see the right role? Submit your resume.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-muted-foreground">
                We match candidates to openings as they arise. Share your profile and a recruiter will reach out when there's a fit.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <div className="border border-border p-6 sm:p-10">
                <ApplicationForm source="talent-network" tid="app-talent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Apply modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-2xl" data-testid="job-modal">
          {active && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {active.requisition_id && <span className="text-brand">{active.requisition_id}</span>}
                  <span>{active.location}</span>
                  <span>{active.employmentType}</span>
                  {active.work_arrangement && <span>{active.work_arrangement}</span>}
                  {active.clearance_requirement && active.clearance_requirement !== "None" && <span>{active.clearance_requirement}</span>}
                </div>
                <DialogTitle className="mt-3 font-display text-3xl font-light tracking-tighter text-navy">{active.title}</DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-3">
                {formatPay(active) && <DetailCell icon={DollarSign} label="Compensation" value={formatPay(active)} />}
                {active.education_requirement && active.education_requirement !== "None" && <DetailCell icon={GraduationCap} label="Education" value={active.education_requirement} />}
                {(active.experience_years || active.experience_years === 0) && <DetailCell icon={Clock} label="Experience" value={`${active.experience_years}+ yrs`} />}
                {active.anticipated_start_date && <DetailCell icon={CalendarDays} label="Start" value={active.anticipated_start_date} />}
                {active.active_clearance_required && <DetailCell icon={ShieldCheck} label="Clearance" value="Active required" />}
                {active.hiring_status && <DetailCell icon={Briefcase} label="Hiring" value={active.hiring_status} />}
              </div>

              {active.specialized_experience && (
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Required specialized experience</div>
                  <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{active.specialized_experience}</p>
                </div>
              )}
              {active.fringe_benefits && (
                <p className="text-sm text-muted-foreground"><span className="font-medium text-navy">Benefits: </span>{active.fringe_benefits}</p>
              )}

              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{active.description}</p>

              {(active.applyMode === "external" || active.applyMode === "both") && active.externalUrl && (
                <a
                  href={active.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="job-external-apply"
                  className="inline-flex items-center gap-2 border border-border px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"
                >
                  Apply on external site <ExternalLink size={15} />
                </a>
              )}

              {(active.applyMode === "internal" || active.applyMode === "both") && (
                <div className="border-t border-border pt-6">
                  <div className="mb-5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Apply directly</div>
                  <ApplicationForm job={active} source="job" tid="app-modal" />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <Icon size={15} className="text-brand" /> {label}
    </span>
  );
}

function DetailCell({ icon: Icon, label, value }) {
  return (
    <div className="bg-card p-4">
      <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground"><Icon size={12} className="text-brand" /> {label}</div>
      <div className="mt-1.5 text-sm font-medium text-navy">{value}</div>
    </div>
  );
}

function formatPay(job) {
  const min = job.pay_min, max = job.pay_max;
  if (!min && !max) return "";
  const unit = job.pay_unit === "hour" ? "/hr" : "/yr";
  const fmt = (n) => "$" + Number(n).toLocaleString();
  if (min && max) return `${fmt(min)} – ${fmt(max)}${unit}`;
  return `${fmt(min || max)}${unit}`;
}
